<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Under_the_Hood/Core_Interfaces';
    let nextLink= '/Documentation/Under_the_Hood/FreTool_Interfaces';
    
</script>
<PrevNextSection {prevLink} {nextLink} />
<SectionComponent tag="h1" 
  id="the-freon-editor-interfaces-1"
  bind:intersecting={visibleById["the-freon-editor-interfaces-1"]}
  onIntersectChange={(visible) => setVisible("the-freon-editor-interfaces-1", visible)}
>The Freon Editor Interfaces</SectionComponent>
<p>There are two series of interfaces that make Freon and its generated code extensible and flexible.<br>
The first series are the <a
  href="/Documentation/Under_the_Hood/FreTool_Interfaces"
>interfaces of the generated code</a>.</p>
<p>The second series of interfaces define the <strong>editor framework</strong> itself.</p>
<SectionComponent tag="h2" 
  id="freprojection-2"
  bind:intersecting={visibleById["freprojection-2"]}
  onIntersectChange={(visible) => setVisible("freprojection-2", visible)}
>FreProjection</SectionComponent>
<p>The projection from your model or AST is defined by implementing the <code>FreProjection</code> interface.</p>
<pre class="language-ts"><code class="language-ts">/* File: core/src/editor/projections/FreProjection.ts  */

/**
 * Interface for a custom projection.
 */
export interface FreProjection &#123;
	// todo add priority

	// Name of the custom projection
	name: string;

	// The projection handler that this projection is registered in.
	handler: FreProjectionHandler;

	// A map from the name of the concept (or the freLanguageConcept() of the FreElement node) to
	// the function that may return the custom box for a node of that type.
	nodeTypeToBoxMethod: Map&lt;string, (node: FreNode) =&gt; Box&gt;;

	// A map from the name of the concept (or the freLanguageConcept() of the FreElement node) to
	// the function that may return the custom box for a node of that type.
	nodeTypeToTableDefinition: Map&lt;string, () =&gt; FreTableDefinition&gt;; // todo change name and remove Tabledefinition type
&#125;</code></pre>
<p>Implementing this interface should return a <strong>Box</strong> for each <code>element</code> in the AST.<br>
Freon includes many predefined boxes (textual, tabular, horizontal or vertical collections, etc.)<br>
to define this projection.</p>
<p>The box model is further described in the <a
  href="/Documentation/Under_the_Hood/Editor_Framework#projection-layout-based-on-boxes-3"
>Editor Framework documentation</a>.</p>
<SectionComponent tag="h2" 
  id="freexpressionnode-3"
  bind:intersecting={visibleById["freexpressionnode-3"]}
  onIntersectChange={(visible) => setVisible("freexpressionnode-3", visible)}
>FreExpressionNode</SectionComponent>
<p>An object that implements <code>FreExpressionNode</code> represents a typed node within the model AST that behaves as an expression.</p>
<pre class="language-ts"><code class="language-ts">/* File: core/src/language/FreExpression.ts */

export interface FreExpressionNode extends FreNode &#123;&#125;</code></pre>
<SectionComponent tag="h2" 
  id="frebinaryexpression-4"
  bind:intersecting={visibleById["frebinaryexpression-4"]}
  onIntersectChange={(visible) => setVisible("frebinaryexpression-4", visible)}
>FreBinaryExpression</SectionComponent>
<p>An object that implements <code>FreBinaryExpression</code> is a node in the model AST.<br>
It represents an expression that has exactly two operands, which are usually written to the left and right of the operator symbol (for example, <code>4 + 5</code>).</p>
<p>This interface extends <a
  href="/Documentation/Under_the_Hood/Editor_Interfaces#freexpressionnode-3"
><code>FreExpression</code></a>.</p>
<pre class="language-ts"><code class="language-ts">/* File: core/src/language/FreBinaryExpression.ts */

export interface FreBinaryExpression extends FreExpressionNode &#123;
	freLeft(): FreExpressionNode;

	freSetLeft(left: FreExpressionNode): void;

	freRight(): FreExpressionNode;

	freSetRight(right: FreExpressionNode): void;

	frePriority(): number;
&#125;</code></pre>
<p>In both series of interfaces, the following two core interfaces are reused:</p>
<ul>
<li><a href="/Documentation/Under_the_Hood/Core_Interfaces#frenode-2">FreNode</a></li>
<li><a
  href="/Documentation/Under_the_Hood/Core_Interfaces#frenamednode-3"
>FreNamedNode</a></li>
</ul>


<PrevNextSection {prevLink} {nextLink} />